/* eslint-disable @typescript-eslint/no-explicit-any */
// Pobranie dostępnej kamery i rozpoczęcie nagrywania
// do rozważenia implementacja wyłączenie kamery, teraz dzieje się to automatycznie poprzez przeładowanie strony
import * as Sentry from '@sentry/browser';
import $ from 'jquery';
import Peer from 'peerjs';
import io from 'socket.io-client';

import {
  DEBUG_MODE,
  defaultMonitorRoomId,
  getUrlChatForExam,
  getUrlTurnCredentials,
  urlPeer2PeerServer,
} from '../spdConfig';

import { getActiveToken } from './auth';
import { SupervisorMonitor, SurveyInformation } from './supervisorMonitor';
import { Announcement } from './announcement';
import { TextSelector } from './textSelector';
import { Loader } from './loader';

declare let supervisorMonitor: SupervisorMonitor;
declare let defaultDbTextSelector: TextSelector;

const joinPaths = (...paths: string[]): string => {
  return ('/' + paths.join('/')).replace(/\/+/g, '/');
};

export class StreamCommunication {
  protected socket;
  protected peer: Peer | null = null;
  protected surveyId: string | null = null;
  protected mediaStream: MediaStream | null = null;
  protected monitorRoomID = defaultMonitorRoomId;
  private currentFocusMessageId: string | null = null;
  private focusMessageSeq = 0;
  private monitorIds: Set<string> = new Set();
  private recoveryTimerId: number | null = null;
  private recoveryInProgress = false;
  private destroyed = false;
  private cachedIceServers: RTCIceServer[] = [];

  private escapeHtml(value: string) {
    return $('<div>').text(value).html();
  }

  private normalizeMonitorId(raw: unknown): string {
    if (typeof raw === 'string') {
      return raw.trim();
    }
    if (raw && typeof raw === 'object') {
      const candidate = raw as {
        monitorId?: unknown;
        userId?: unknown;
        id?: unknown;
      };
      if (typeof candidate.monitorId === 'string') {
        return candidate.monitorId.trim();
      }
      if (typeof candidate.userId === 'string') {
        return candidate.userId.trim();
      }
      if (typeof candidate.id === 'string') {
        return candidate.id.trim();
      }
    }
    return '';
  }

  private dispatchCameraDisconnected() {
    window.dispatchEvent(new CustomEvent('monitor-camera-disconnected'));
  }

  private dispatchCameraRecovered() {
    window.dispatchEvent(new CustomEvent('monitor-camera-recovered'));
  }

  private getCurrentVideoTrack(): MediaStreamTrack | null {
    return this.mediaStream?.getVideoTracks()[0] || null;
  }

  private hasUsableMediaStream() {
    const track = this.getCurrentVideoTrack();
    return !!track && track.readyState === 'live' && !track.muted;
  }

  private stopMediaStream() {
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
      this.mediaStream = null;
    }
  }

  private attachStreamListeners(stream: MediaStream) {
    const videoTrack = stream.getVideoTracks()[0];
    if (!videoTrack) {
      return;
    }
    videoTrack.onmute = () => {
      if (this.destroyed) {
        return;
      }
      DEBUG_MODE && console.warn('Monitor camera track muted');
      this.dispatchCameraDisconnected();
      this.startRecoveryLoop();
    };
    videoTrack.onunmute = () => {
      if (this.destroyed) {
        return;
      }
      DEBUG_MODE && console.log('Monitor camera track unmuted');
      if (this.hasUsableMediaStream()) {
        this.dispatchCameraRecovered();
        this.connectKnownMonitors();
        this.clearRecoveryLoop();
      }
    };
    videoTrack.onended = () => {
      if (this.destroyed) {
        return;
      }
      DEBUG_MODE && console.warn('Monitor camera track ended');
      this.stopMediaStream();
      this.dispatchCameraDisconnected();
      this.startRecoveryLoop();
    };
  }

  private clearRecoveryLoop() {
    if (this.recoveryTimerId) {
      window.clearInterval(this.recoveryTimerId);
      this.recoveryTimerId = null;
    }
  }

  private connectMonitorVideo(monitorId: string) {
    if (!this.peer || !this.mediaStream) {
      return;
    }
    DEBUG_MODE &&
      console.log('Send stream and SurveyInformation to:', monitorId);
    const mediaConnection = this.peer.call(monitorId, this.mediaStream);
    if (mediaConnection) {
      mediaConnection.on('error', err => {
        DEBUG_MODE &&
          console.error('[monitor-connect] media connection error', {
            monitorId,
            err,
            iceServers: this.cachedIceServers,
          });
      });
      mediaConnection.on('close', () => {
        DEBUG_MODE &&
          console.warn('[monitor-connect] media connection closed', {
            monitorId,
          });
      });
      mediaConnection.on('stream', () => {
        DEBUG_MODE &&
          console.log('[monitor-connect] remote accepted stream', {
            monitorId,
          });
      });
    } else {
      DEBUG_MODE &&
        console.error('[monitor-connect] peer.call returned null', {
          monitorId,
        });
    }
    supervisorMonitor.sendSurveyInformation();
  }

  private connectKnownMonitors() {
    this.monitorIds.forEach(monitorId => this.connectMonitorVideo(monitorId));
  }

  private async tryAcquireStream() {
    const stream = await this.getMonitorVideoStream();
    this.stopMediaStream();
    this.mediaStream = stream;
    this.attachStreamListeners(stream);
    if (this.hasUsableMediaStream()) {
      this.dispatchCameraRecovered();
      this.connectKnownMonitors();
      return;
    }
    DEBUG_MODE &&
      console.warn(
        'Monitor camera stream acquired but still not usable (muted/not-live)',
      );
    this.dispatchCameraDisconnected();
    this.startRecoveryLoop();
  }

  private startRecoveryLoop() {
    if (this.recoveryTimerId || this.destroyed) {
      return;
    }
    this.recoveryTimerId = window.setInterval(async () => {
      if (
        this.recoveryInProgress ||
        this.destroyed ||
        this.hasUsableMediaStream()
      ) {
        return;
      }
      this.recoveryInProgress = true;
      try {
        await this.tryAcquireStream();
        if (this.hasUsableMediaStream()) {
          this.clearRecoveryLoop();
        }
      } catch (err) {
        DEBUG_MODE &&
          console.warn('Monitor camera recovery attempt failed', err);
      } finally {
        this.recoveryInProgress = false;
      }
    }, 3000);
  }

  constructor(activeToken: string) {
    const connectionUrl = new URL(urlPeer2PeerServer);
    const baseUrl = connectionUrl.protocol + '//' + connectionUrl.host;
    const path = joinPaths(connectionUrl.pathname, '/socket.io');

    this.socket = io(baseUrl, {
      path: path,
      auth: { token: activeToken, consultant: false },
    });
    this.socket.on('connect_error', async (err: any) => {
      DEBUG_MODE && console.log('connect_error', err.message, err.name);
      if (err.message === 'Authentication error') {
        this.socket.auth = { token: await getActiveToken(), consultant: false };
        this.socket.connect();
      }
    });
    this.socket.on('error', (err: any) => {
      DEBUG_MODE && console.log('ERROR ', err.message, err.name);
    });
    this.socket.on('invalid-token', async () => {
      DEBUG_MODE && console.log('invalid-token');
    });
    this.socket.on('disconnect', (reason: string) => {
      DEBUG_MODE && console.log(`Disconnected from server: ${reason}`);
    });
    this.socket.on(
      'focus-message',
      async (payload: {
        messageId: string;
        text: string;
        sentAt: string;
        userName: string;
        type: 'focus';
      }) => {
        await this.handleFocusMessage(payload);
      },
    );
    this.socket.on(
      'createMessage',
      (payload: {
        messageId: string;
        text: string;
        sentAt: string;
        userName: string;
        type: 'chat';
      }) => {
        DEBUG_MODE && console.log('chat message received', payload);
      },
    );
    this.socket.on(
      'monitor-connected',
      async (userId: string, roomId: string) => {
        DEBUG_MODE &&
          console.log('Monitor connected ', userId, 'ROOM:', roomId);
        const normalizedUserId = this.normalizeMonitorId(userId);
        if (normalizedUserId) {
          this.monitorIds.add(normalizedUserId);
          this.connectMonitorVideo(normalizedUserId);
        }
        //raz jeszcze podłączam się do monitora
        const peerId = this.peer?.id;
        if (peerId && this.surveyId) {
          this.socket.emit(
            'join-monitor',
            await getActiveToken(),
            roomId,
            this.surveyId,
            this.peer?.id,
            false,
          );
        }
      },
    );
  }

  private async handleFocusMessage(payload: {
    messageId: string;
    text: string;
    sentAt: string;
    userName: string;
    type: 'focus';
  }) {
    const { messageId, text, userName } = payload;
    const prefixText = defaultDbTextSelector.getText('focus_message_prefix');
    const prefix = prefixText.text_presentation;
    const readPrefix = prefixText.text_read || prefixText.text_presentation;
    const label = this.escapeHtml(userName || '-');
    const safeText = this.escapeHtml(text);
    const messageHtml =
      `<div class="focus-message">` +
      `<div class="focus-message-title"><strong>${prefix}</strong></div>` +
      `<div class="focus-message-body"><strong>${label}</strong>:<br/>` +
      `${safeText}` +
      `</div>` +
      `</div>`;
    const readText = `${readPrefix} ${userName || '-'}: ${text}`;
    const seq = ++this.focusMessageSeq;
    this.currentFocusMessageId = messageId;
    Announcement.forceCloseAnnouncementModal();
    await Announcement.announcementCustomAlert(
      messageHtml,
      true,
      0,
      readText,
      true,
    );
    const shouldSendRead =
      this.focusMessageSeq === seq && this.currentFocusMessageId === messageId;
    if (shouldSendRead) {
      this.socket.emit('message-read', {
        messageId,
        readAt: new Date().toISOString(),
      });
      this.currentFocusMessageId = null;
    }
  }

  public async sendCallForHelp(surveyId: string | undefined) {
    DEBUG_MODE &&
      console.log('sendCallForHelp to MONITOR', this.monitorRoomID, surveyId);
    this.socket.emit(
      'help',
      await getActiveToken(),
      this.monitorRoomID,
      surveyId,
    );
  }

  public async sendSurveyInformation(
    surveyId: string,
    information: SurveyInformation,
  ) {
    DEBUG_MODE &&
      console.log(
        'sendSurveyInformation',
        this.monitorRoomID,
        surveyId,
        information,
      );
    this.socket.emit(
      'survey-information',
      await getActiveToken(),
      this.monitorRoomID,
      surveyId,
      information,
    );
  }

  private async fetchIceServers() {
    try {
      const url = getUrlTurnCredentials(await getActiveToken());
      const data = await Loader.getSyncJSONWithVersion(url);

      if (data && data.iceServers && Array.isArray(data.iceServers)) {
        this.cachedIceServers = data.iceServers as RTCIceServer[];
        DEBUG_MODE &&
          console.log(
            '[monitor-ice] fetched iceServers',
            this.cachedIceServers,
          );
        DEBUG_MODE && console.log('ICE servers loaded successfully');
        return data.iceServers;
      }
      DEBUG_MODE &&
        console.warn(
          '[monitor-ice] turn-credentials response without iceServers',
        );
    } catch (error) {
      Sentry.captureException(error);
      DEBUG_MODE && console.error('Error fetching ICE servers:', error);
      DEBUG_MODE &&
        console.error('[monitor-ice] failed to fetch iceServers', error);
    }

    this.cachedIceServers = [];
    return [];
  }

  /**
   * Stream dla monitora: preferuje wyższą rozdzielczość, ale bez twardego `min`
   * (OverconstrainedError na słabszych / wirtualnych kamerach). Przy błędzie
   * robi jeden fallback do domyślnych ustawień kamery.
   */
  private async getMonitorVideoStream(): Promise<MediaStream> {
    try {
      return await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
    } catch (err: unknown) {
      const name =
        err && typeof err === 'object' && 'name' in err
          ? String((err as { name: string }).name)
          : '';
      if (
        name !== 'OverconstrainedError' &&
        name !== 'ConstraintNotSatisfiedError'
      ) {
        throw err;
      }
      DEBUG_MODE &&
        console.warn(
          'getUserMedia: fallback to default video constraints',
          name,
          err,
        );
      return navigator.mediaDevices.getUserMedia({
        video: true,
        audio: false,
      });
    }
  }

  protected async runStreamingEngine() {
    console.log('runStreamingEngine for survey, peer', this.peer);
    this.destroyed = false;
    this.clearRecoveryLoop();
    this.monitorIds.clear();
    if (this.peer) {
      this.peer.destroy();
      this.peer = null;
    }
    const peerServerUrl = new URL(urlPeer2PeerServer);
    const peer = new Peer({
      host: peerServerUrl.hostname,
      port: peerServerUrl.port
        ? parseInt(peerServerUrl.port)
        : peerServerUrl.protocol === 'https:'
        ? 443
        : 80,
      debug: 2,
      secure: peerServerUrl.protocol === 'https:',
      path: '/peerjs',
      config: { iceServers: await this.fetchIceServers() },
    });
    peer.on('error', err => {
      DEBUG_MODE &&
        console.error('[monitor-peer] peer error', {
          err,
          surveyId: this.surveyId,
          iceServers: this.cachedIceServers,
          userAgent: navigator.userAgent,
        });
    });
    this.peer = peer;
    peer.on('open', id => {
      DEBUG_MODE &&
        console.log('PeerId of exam', id, 'SurveyId', this.surveyId);
      void (async () => {
        this.socket.emit(
          'join-monitor',
          await getActiveToken(),
          this.monitorRoomID,
          this.surveyId,
          id,
          false,
        );
      })();
      peer.on('connection', conn => {
        DEBUG_MODE && console.log('connection with moinitor');
        conn.on('data', monitorId => {
          const monitorIdValue = this.normalizeMonitorId(monitorId);
          if (!monitorIdValue) {
            DEBUG_MODE &&
              console.warn(
                'Received monitor id in unexpected format',
                monitorId,
              );
            return;
          }
          DEBUG_MODE &&
            console.log('Receive data with monitor id', monitorIdValue);
          this.monitorIds.add(monitorIdValue);
          this.connectMonitorVideo(monitorIdValue);
        });
      });

      void this.tryAcquireStream().catch(err => {
        DEBUG_MODE &&
          console.error('An error occurred during turnOnCamera:', err);
        this.dispatchCameraDisconnected();
        this.startRecoveryLoop();
      });
    });
  }

  public startStreaming(surveyId: string) {
    DEBUG_MODE && console.log('startStreaming for survey', surveyId);
    this.surveyId = surveyId;

    this.socket.on('authentication-success', () => {
      DEBUG_MODE && console.log('authentication-success');
    });

    this.socket.on('open-chat', async (roomId: string, userId: string) => {
      DEBUG_MODE && console.log('open-chat', roomId, userId, surveyId);
      await Announcement.announcementAlert('remote_consultation');
      const windowObjectReference = window.open(
        getUrlChatForExam(surveyId, await getActiveToken()),
        '_blank',
        'noopener',
      );
      windowObjectReference && windowObjectReference.focus();
    });

    this.socket.on('connect', async () => {
      DEBUG_MODE &&
        console.log(
          'connect with monitor - socked.id',
          this.socket.id,
          this.socket.connected,
        );

      await this.runStreamingEngine();
    });
  }

  public destroy() {
    this.destroyed = true;
    this.clearRecoveryLoop();
    this.monitorIds.clear();
    this.stopMediaStream();
    if (this.peer) {
      this.peer.destroy();
      this.peer = null;
    }
    if (this.socket) {
      this.socket.disconnect();
    }
  }
}
